import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import API, { UPLOADS_URL } from '../api/axios';

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('/frontend/')) return src.replace('/frontend/', '/');
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const DEFAULT_SECTIONS = [
  { section_key: 'hero', section_type: 'hero', title: 'Extra-Curricular Activities', image: '/images/page-banners/extra-curricullam.jpg' },
  {
    section_key: 'intro',
    section_type: 'intro',
    kicker: 'Holistic Development',
    title: 'Beyond Academics — Learning Through Passion & Play',
    description: 'At St. Andrews World School, Indirapuram, we believe that education extends far beyond textbooks. Our vibrant co-curricular and sports programmes are designed to nurture physical fitness, creative expression, leadership, critical thinking, and character in every child.',
  },
];

const ExtraCurriculumActivitiesPage = () => {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [pageData, setPageData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const [secRes, actRes, pageRes] = await Promise.allSettled([
          API.get('/extra-curricular/sections'),
          API.get('/co-curricular-activities/public'),
          API.get('/pages/public/extra-curricular-activities'),
        ]);

        if (!mounted) return;

        if (secRes.status === 'fulfilled' && secRes.value.data?.data?.length) {
          setSections(secRes.value.data.data);
        }

        if (actRes.status === 'fulfilled' && actRes.value.data?.data?.length) {
          setActivities(actRes.value.data.data);
        }

        if (pageRes.status === 'fulfilled' && pageRes.value.data?.data) {
          setPageData(pageRes.value.data.data);
        }
      } catch (err) {
        /* ignore */
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, []);

  const heroSec = sections.find((s) => s.section_type === 'hero');
  const introSec = sections.find((s) => s.section_type === 'intro') || DEFAULT_SECTIONS.find((s) => s.section_type === 'intro');

  const title = heroSec?.title || pageData?.title || 'Extracurricular Activities';

  if (loading) return <div className="loading-screen"><i className="fas fa-spinner fa-spin"></i></div>;

  const bannerImg = heroSec?.image
    ? resolveImg(heroSec.image)
    : (pageData?.banner_image
        ? resolveImg(pageData.banner_image)
        : '/images/page-banners/extra-curricullam.jpg');

  return (
    <>
      <SEO
        slug="extra-curricular-activities"
        title={title}
        description="Extracurricular and co-curricular activities, sports, performing arts, and clubs at St. Andrews World School, Indirapuram."
        keywords="Extracurricular Activities, Sports, Football, Taekwondo, Swimming, Arts, SAWS Indirapuram"
      />

      {/* PAGE HERO */}
      <section className="page-hero">
        <img
          src={bannerImg}
          alt={title}
          className="page-hero-img"
          loading="eager"
          onError={(e) => {
            if (!e.target.dataset.tried) {
              e.target.dataset.tried = 1;
              e.target.src = '/images/page-banners/extra-curricullam.jpg';
            }
          }}
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner" />
          </div>
        </div>
      </section>

      {/* INTRO CONTENT */}
      {introSec && (
        <section className="py-14 md:py-18 bg-white relative overflow-hidden">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center">
              <Reveal>
                {introSec.kicker && <div className="kicker">{introSec.kicker}</div>}
                <h1 className="text-[28px] md:text-[38px] font-bold text-primary mt-2 leading-tight">
                  {introSec.title || 'Beyond Academics — Learning Through Passion & Play'}
                </h1>
                <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mx-auto my-5"></div>
                {introSec.description && (
                  <div className="text-body text-[16px] md:text-[18px] leading-relaxed space-y-4">
                    {introSec.description.split(/\n\s*\n/).map((para, i) => (
                      <p key={i}>{para.trim()}</p>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>

            {pageData?.content && (
              <div className="max-w-4xl mx-auto mt-10">
                <Reveal delayMs={100}>
                  <div className="bg-light rounded-2xl p-6 md:p-8 border border-gray-100 text-body leading-relaxed prose max-w-none" dangerouslySetInnerHTML={{ __html: pageData.content }} />
                </Reveal>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ACTIVITIES LISTING GRID */}
      <section className="py-14 md:py-20 bg-light">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="kicker">Our Activities</div>
              <h2 className="text-[26px] md:text-[34px] font-bold text-primary mt-2 leading-tight">
                Explore Our Co-Curricular & Sports Programme
              </h2>
              <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mx-auto mt-4"></div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {activities.map((item, index) => (
              <Reveal key={item.id || item.slug || index} delayMs={index * 50}>
                <article className="group bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full flex flex-col">
                  <div className="relative overflow-hidden h-[240px]">
                    <img
                      src={resolveImg(item.image)}
                      alt={`${item.title} at St. Andrews World School, Indirapuram`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        if (!e.target.dataset.tried) {
                          e.target.dataset.tried = 1;
                          e.target.src = '/images/coactivities/Football.jpg';
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                          <i className={`fas ${item.icon || 'fa-volleyball'}`} />
                        </div>
                        <h3 className="text-primary font-bold text-lg leading-snug">{item.title}</h3>
                      </div>
                      {item.description ? (
                        <div
                          className="text-body text-[14px] leading-relaxed text-gray-600 line-clamp-4"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      ) : (
                        <p className="text-body text-[14px] leading-relaxed text-gray-600">
                          Engaging activities promoting agility, teamwork, and confidence under qualified coaches.
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {activities.length === 0 && (
            <p className="text-center text-gray-400 py-10">Loading activities...</p>
          )}
        </div>
      </section>

      {/* ADMISSION INQUIRY STRIP */}
      <section className="py-12 bg-primary text-white text-center">
        <div className="container">
          <h3 className="text-2xl md:text-3xl font-bold">Discover the Best Opportunities for Your Child</h3>
          <p className="text-white/80 mt-2 max-w-2xl mx-auto text-sm md:text-base">
            Admissions open for the upcoming academic session. Experience our world-class infrastructure and diverse activities first-hand.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link to="/admission" className="btn-gold">Enquire for Admission</Link>
            <Link to="/contact-us" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-white/30 text-white bg-white/10 hover:bg-white/20 transition-all">
              Schedule Campus Visit
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ExtraCurriculumActivitiesPage;
